import ReactDOM from 'react-dom/client';
import React, { useMemo } from 'react';
import { Document } from '../services/http/documents.api';
import Badge from '../components/Badge/Badge';
import { DocumentTransferMimeType } from '../utils/document.utils';

const pdfIcon = `<svg id="Pdf_icon" data-name="Pdf icon" xmlns="http://www.w3.org/2000/svg" width="19.774" height="23.07" viewBox="0 0 19.774 23.07">
  <path id="Icon_metro-file-pdf" data-name="Icon metro-file-pdf" d="M21.47,7.1a3,3,0,0,1,.618.978,2.971,2.971,0,0,1,.257,1.133V24.037a1.231,1.231,0,0,1-1.236,1.236H3.807a1.231,1.231,0,0,1-1.236-1.236V3.439A1.231,1.231,0,0,1,3.807,2.2H15.342a2.972,2.972,0,0,1,1.133.257,3,3,0,0,1,.978.618ZM15.754,3.954V8.795h4.841a1.407,1.407,0,0,0-.283-.528l-4.03-4.029a1.406,1.406,0,0,0-.528-.283ZM20.7,23.625V10.443H15.342a1.231,1.231,0,0,1-1.236-1.236V3.851H4.219V23.625H20.7Z" transform="translate(-2.571 -2.204)" fill="#e50019"/>
  <text id="_.PDF" data-name=".PDF" transform="translate(4.264 14.777)" fill="#e50019" font-size="5" font-family="NotoSans-Bold, Noto Sans" font-weight="700"><tspan x="0" y="0">.PDF</tspan></text>
</svg>`;

const pdfIconSemitransparent = `<svg id="Pdf_icon" data-name="Pdf icon" xmlns="http://www.w3.org/2000/svg" width="19.774" height="23.07" viewBox="0 0 19.774 23.07">
  <path id="Icon_metro-file-pdf" data-name="Icon metro-file-pdf" d="M21.47,7.1a3,3,0,0,1,.618.978,2.971,2.971,0,0,1,.257,1.133V24.037a1.231,1.231,0,0,1-1.236,1.236H3.807a1.231,1.231,0,0,1-1.236-1.236V3.439A1.231,1.231,0,0,1,3.807,2.2H15.342a2.972,2.972,0,0,1,1.133.257,3,3,0,0,1,.978.618ZM15.754,3.954V8.795h4.841a1.407,1.407,0,0,0-.283-.528l-4.03-4.029a1.406,1.406,0,0,0-.528-.283ZM20.7,23.625V10.443H15.342a1.231,1.231,0,0,1-1.236-1.236V3.851H4.219V23.625H20.7Z" transform="translate(-2.571 -2.204)" fill="#d5d5d5"/>
  <text id="_.PDF" data-name=".PDF" transform="translate(4.264 14.777)" fill="#d5d5d5" font-size="5" font-family="NotoSans-Bold, Noto Sans" font-weight="700"><tspan x="0" y="0">.PDF</tspan></text>
</svg>
`;

const getSvgBase64 = (hasPdf: boolean) => `data:image/svg+xml;base64,${btoa(hasPdf ? pdfIcon : pdfIconSemitransparent)}`;

const getDocumentsToTransfer = (
  documentItem: Document,
  selectedDocumentsRef: React.MutableRefObject<Document[]>,
  folder: { id: number } | null,
) => {
  const filteredDocuments = selectedDocumentsRef.current.filter(
    (doc) => doc.requiredTagInputFlg === 1,
  );

  const docItem = filteredDocuments.find(({ id }) => id === documentItem.id);
  if (!docItem) filteredDocuments.push(documentItem);

  const selectedDocumentsToTransfer = filteredDocuments.map((doc) => {
    const docFolderId = folder ? folder.id : doc.folderId;
    return { document: doc, folderId: docFolderId };
  });

  return selectedDocumentsToTransfer;
};

const createDragContainer = (): HTMLDivElement => {
  const dragContainer = document.createElement('div');
  Object.assign(dragContainer.style, {
    position: 'fixed',
    top: '0px',
    left: '-9999px',
    zIndex: '9999',
    pointerEvents: 'none',
    paddingBottom: '10px',
  });

  return dragContainer;
};

const createContainer = (): HTMLDivElement => {
  const container = document.createElement('div');
  Object.assign(container.style, {
    width: '200px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '10px',
    backgroundColor: 'white',
    border: '1px solid #D5D5D5',
    borderRadius: '8px',
    fontSize: '14px',
    textAlign: 'center',
    boxShadow: '5px 5px 5px rgba(0, 0, 0, 0.1)',
  });

  return container;
};

const createBadge = (badgeContent: number): HTMLDivElement => {
  const badge = document.createElement('div');

  ReactDOM.createRoot(badge).render(
    <div style={{ position: 'absolute', top: -10, right: 0 }}>
      <Badge
        badgeContent={badgeContent}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      />
    </div>,
  );

  return badge;
};

const createDragIcon = (hasPdf: boolean): HTMLDivElement => {
  const icon = document.createElement('div');
  icon.style.backgroundImage = `url('${getSvgBase64(hasPdf)}')`;
  icon.style.backgroundSize = 'contain';
  icon.style.backgroundRepeat = 'no-repeat';
  icon.style.width = '20px';
  icon.style.height = '20px';
  icon.style.marginRight = '4px';
  return icon;
};

const createTextNode = (text: string): HTMLSpanElement => {
  const textNode = document.createElement('span');
  Object.assign(textNode.style, {
    whiteSpace: 'nowrap',
    maxWidth: '170px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });
  textNode.innerText = text;
  return textNode;
};

const createDragPreview = (selectedDocument: Document, total: number): HTMLDivElement => {
  const dragContainer = createDragContainer();
  const container = createContainer();
  const badge = createBadge(total);
  const checkIcon = createDragIcon(selectedDocument.fileName !== '');
  const textNode = createTextNode(selectedDocument.name);

  container.append(badge, checkIcon, textNode);
  dragContainer.append(container);
  document.body.appendChild(dragContainer);

  return dragContainer;
};

const setDragData = (
  dragEvent: React.DragEvent,
  selectedDocumentsToTransfer: { document: Document, folderId: number }[],
  folder: { id?: number } | null,
) => {
  const { userAgent } = window.navigator;
  const isIE = userAgent.indexOf('Trident/') >= 0;

  dragEvent.dataTransfer.setData(
    isIE ? 'text' : DocumentTransferMimeType,
    JSON.stringify({
      documentsTransfer: selectedDocumentsToTransfer,
      folderId: folder?.id,
    }),
  );
};

const attachDragPreview = (dragEvent: React.DragEvent, dragPreview: HTMLDivElement) => {
  dragEvent.dataTransfer.setDragImage(dragPreview, 0, 5);

  const cleanup = () => document.body.removeChild(dragPreview);
  dragEvent.currentTarget.addEventListener('dragend', cleanup, { once: true });
};

const useDragAndDrop = (folder: { id: number } | null, selectedDocumentsRef: React.MutableRefObject<Document[]>) => {
  const onDragStart = useMemo(
    () => (dragEvent: React.DragEvent<HTMLDivElement>, documentItem: Document) => {
      const selectedDocumentsToTransfer = getDocumentsToTransfer(documentItem, selectedDocumentsRef, folder);

      setDragData(dragEvent, selectedDocumentsToTransfer, folder);

      const dragPreview = createDragPreview(documentItem, selectedDocumentsToTransfer.length);

      attachDragPreview(dragEvent, dragPreview);
    },
    [folder, selectedDocumentsRef],
  );

  return { onDragStart };
};

export default useDragAndDrop;
